"use client";

import { Fragment, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { useRestored } from "@/components/shell/restored";

/**
 * The door, drawn anew when the browser brings it back (Issue 601).
 *
 * The forms here that succeed by leaving — signing in, the six digits that
 * finish signing up, the button on /logout — hold their button on 「请稍候…」
 * so a second press cannot send the request twice, and a full page load
 * takes over (`window.location.href`), so nothing ever puts the button back.
 * Brought back from the back-forward cache, the page was the one it was left
 * as: the button stuck, and the fields as they were typed, password and six
 * digits included.
 *
 * The second half is why this starts the page over rather than setting the
 * button free. Freed, a page brought back after someone signed in — and
 * later out — on a shared machine is their address and password under a
 * button that works. Started over, it is what the browser draws when it does
 * not keep the page, which is what today's Chrome does: an empty form.
 *
 * All of it and not a list of fields to reset, because a list is the thing
 * that goes stale — the next field, the next door, and the one left out may
 * be the password. What is lost is a half-typed address when they went to
 * another page and came back, which is the same as loading it again.
 *
 * Started over inside the event, as NavigationPending lets go of its lock:
 * left to a later render, the page they come back to is first drawn as it
 * was left.
 */
export function FreshOnReturn({ children }: { children: ReactNode }) {
  const [round, setRound] = useState(0);
  useRestored(() => flushSync(() => setRound((n) => n + 1)));
  return <Fragment key={round}>{children}</Fragment>;
}
